/* ==========================================================================
   Ozeret -- notifications on this device

   Where a device can stand:

     unsupported   the browser cannot do it at all
     needs-install an iPhone or iPad in Safari: Apple only allows
                   notifications once the app is on the home screen
     blocked       they said no, and only the browser's settings can undo it
     off           allowed, or never asked, but not turned on here
     on            this device will buzz

   The browser's permission is per site, the subscription is per device, and
   which kinds of news are wanted is per person and kept on the server.
   ========================================================================== */

export type PushState = 'unsupported' | 'needs-install' | 'blocked' | 'off' | 'on';

/** Set when somebody turns notifications off on this device, so that
    switching them back on by ourselves never overrules them. */
const TURNED_OFF = 'ozeret.push.off';
const remember = (off: boolean) => {
  try {
    if (off) localStorage.setItem(TURNED_OFF, '1');
    else localStorage.removeItem(TURNED_OFF);
  } catch { /* storage blocked: nothing to remember with */ }
};
const turnedOffHere = () => {
  try { return localStorage.getItem(TURNED_OFF) === '1'; } catch { return false; }
};

function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function installed(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

const capable = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Start the service worker. Called once, at start-up; harmless if it fails. */
export function registerWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch(() => { /* private mode, old browser */ });
}

export async function pushState(): Promise<PushState> {
  if (!capable()) return isIos() && !installed() ? 'needs-install' : 'unsupported';
  if (isIos() && !installed()) return 'needs-install';
  if (Notification.permission === 'denied') return 'blocked';
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    return sub && Notification.permission === 'granted' ? 'on' : 'off';
  } catch {
    return 'off';
  }
}

function keyBytes(key: string): Uint8Array<ArrayBuffer> {
  const b64 = key.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (key.length % 4)) % 4);
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Ask, subscribe, and tell the server. Must run from a tap: browsers only
    show the permission question in answer to something the person did. */
export async function turnOn(lang: string): Promise<PushState> {
  if (!capable()) return pushState();
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';

  const res = await fetch('/api/push/key', { credentials: 'include' });
  const { key } = (await res.json()) as { key: string | null };
  if (!key) throw new Error('Notifications are not set up on this server yet.');

  const reg = (await navigator.serviceWorker.getRegistration())
    ?? (await navigator.serviceWorker.register('/sw.js'));
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) });
  }
  const saved = await fetch('/api/push/subscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ sub: sub.toJSON(), lang }),
  });
  if (!saved.ok) {
    const body = await saved.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? 'Could not turn notifications on.');
  }
  remember(false);
  return 'on';
}

/** On by default, as far as a browser allows. A site can never switch
    notifications on by itself -- the person has to tap Allow once -- but
    where this browser already allows them (signed in on a second account, a
    subscription the browser rotated, a server that lost it) this quietly
    turns them on and keeps the server's copy current. Never after the
    person turned them off here. */
export async function autoOn(lang: string): Promise<void> {
  if (!capable() || turnedOffHere()) return;
  if (isIos() && !installed()) return;
  if (Notification.permission !== 'granted') return;
  try {
    await turnOn(lang);
  } catch { /* try again next time */ }
}

/** Somewhere the question can be put on this device: notifications are
    possible here and not blocked. Whether it is put depends on askedHere(). */
export function mayAsk(): boolean {
  if (!capable()) return false;
  if (isIos() && !installed()) return false;
  return Notification.permission !== 'denied';
}

/* The question is put once per sign-in, not once per device: people rarely
   sign out, so a new sign-in is the moment worth asking again -- even of
   somebody who turned notifications off before. */
const ASKED = 'ozeret.push.asked';
export function askedHere(): boolean {
  try { return localStorage.getItem(ASKED) === '1'; } catch { return false; }
}
export function markAsked(): void {
  try { localStorage.setItem(ASKED, '1'); } catch { /* asks again next time */ }
}
/** Called on every sign-in. */
export function askAgain(): void {
  try {
    localStorage.removeItem(ASKED);
    /* The texts question, which follows it (PushCard.tsx). */
    localStorage.removeItem('ozeret.texts.asked');
  } catch { /* nothing remembered anyway */ }
}

/** An iPhone or iPad, for wording where to unblock notifications. */
export const onIos = isIos;

export async function turnOff(): Promise<PushState> {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await fetch('/api/push/unsubscribe', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });
      await sub.unsubscribe();
    }
  } catch { /* already gone */ }
  remember(true);
  return pushState();
}

/** Tapping a notification while the app is open: the worker hands over the
    address, and the app goes there. */
export function listenForNotificationTaps(open: (path: string) => void): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.addEventListener('message', (e) => {
    const d = e.data as { type?: string; path?: string } | null;
    if (d?.type === 'ozeret-open' && typeof d.path === 'string' && d.path.startsWith('/')) open(d.path);
  });
}
